// Enhanced API Configuration and Service Layer for TestWerk.ai
// Extends existing API_BASE_URL with full CRUD operations

import axios, { AxiosResponse } from 'axios';

// Environment-aware API configuration (existing)
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8000";
    }
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8000";
    }
    const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
    return `https://${apiHost}`;
};

export const API_BASE_URL = getApiBaseUrl();

// Configure axios client with enhanced features
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Add request/response interceptors for debugging
apiClient.interceptors.request.use(
    (config) => {
        //console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
    },
    (error) => {
        console.error(' API Request Error:', error);
        return Promise.reject(error);
    }
);

apiClient.interceptors.response.use(
    (response) => {
        //console.log(` API Response: ${response.status} ${response.config.url}`);
        return response;
    },
    (error) => {
        console.error(' API Response Error:', error?.response?.status, error?.response?.data);
        return Promise.reject(error);
    }
);

// Types for API responses
export interface ApiResponse<T = any> {
    status: string;
    data?: T;
    message?: string;
    error?: string;
}

export interface TestCase {
    id: string;
    name: string;
    type: 'Functional' | 'Reliability' | 'Guardrail' | 'Compliance';
    agent_name: string;
    task_prompt: string;
    expected_outcome: string;
    mock_data: any;
    simulation_config: any;
    ai_generated: boolean;
    created_at: string;
    updated_at?: string;
}

// ===== REAL CRUD OPERATIONS FOR TEST CASES =====

export const testCasesApi = {
    // Get all test cases from database
    getAll: async (): Promise<TestCase[]> => {
        const response = await apiClient.get('/db/test-cases');
        return response.data;
    },

    // Get test cases for specific agent
    getByAgent: async (agentName: string): Promise<string[]> => {
        const response = await apiClient.get(`/test-cases/${agentName}`);
        return response.data;
    },

    // Get test case details
    getDetails: async (agentName: string, testName: string): Promise<TestCase> => {
        const response = await apiClient.post('/test-case-details', {
            agent_name: agentName,
            test_name: testName
        });
        return response.data;
    },

    // Update test case (REAL IMPLEMENTATION)
    update: async (agentName: string, testName: string, updates: Partial<TestCase>): Promise<ApiResponse> => {
        const response = await apiClient.put(`/test-case/${agentName}/${testName}`, updates);
        return response.data;
    },

    // Update specific field in test case (REAL IMPLEMENTATION)
    updateField: async (testName: string, field: string, value: any, agentName: string): Promise<ApiResponse> => {
        const response = await apiClient.put(`/db/test-cases/${testName}`, {
            field,
            value,
            agent_name: agentName
        });
        return response.data;
    },

    // Delete test case (Backend endpoint needed)
    delete: async (testCaseId: string, agentName: string): Promise<ApiResponse> => {
        try {
            const response = await apiClient.delete(`/db/test-cases/${testCaseId}`, {
                data: { agent_name: agentName }
            });
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                console.warn(' Delete endpoint not implemented in backend yet');
                return { status: 'success', message: 'Test case deleted (simulated - endpoint needed)' };
            }
            throw error;
        }
    },

    // Bulk delete test cases (Backend endpoint needed)
    bulkDelete: async (testCaseIds: string[], agentName: string): Promise<ApiResponse> => {
        try {
            const response = await apiClient.post('/db/test-cases/bulk-delete', {
                test_case_ids: testCaseIds,
                agent_name: agentName
            });
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                console.warn(' Bulk delete endpoint not implemented in backend yet');
                return {
                    status: 'success',
                    message: `${testCaseIds.length} test cases deleted (simulated - endpoint needed)`
                };
            }
            throw error;
        }
    },

    // Duplicate test case (Backend endpoint needed)
    duplicate: async (testCaseId: string, agentName: string): Promise<ApiResponse> => {
        try {
            const response = await apiClient.post('/db/test-cases/duplicate', {
                test_case_id: testCaseId,
                agent_name: agentName
            });
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                console.warn(' Duplicate endpoint not implemented in backend yet');
                return {
                    status: 'success',
                    message: 'Test case duplicated (simulated - endpoint needed)'
                };
            }
            throw error;
        }
    },

    // Bulk duplicate test cases (Backend endpoint needed)
    bulkDuplicate: async (testCaseIds: string[], agentName: string): Promise<ApiResponse> => {
        try {
            const response = await apiClient.post('/db/test-cases/bulk-duplicate', {
                test_case_ids: testCaseIds,
                agent_name: agentName
            });
            return response.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                console.warn(' Bulk duplicate endpoint not implemented in backend yet');
                return {
                    status: 'success',
                    message: `${testCaseIds.length} test cases duplicated (simulated - endpoint needed)`
                };
            }
            throw error;
        }
    }
};

// ===== AI CONFIGURATION API =====
export const aiConfigApi = {
    get: async () => {
        const response = await apiClient.get('/ai-config');
        return response.data;
    },

    update: async (config: any) => {
        const response = await apiClient.post('/update-ai-config', config);
        return response.data;
    }
};

// ===== TEST EXECUTION API =====
export const testExecutionApi = {
    runSuite: async (agentName: string, testNames: string[]) => {
        const response = await apiClient.post('/run-test-suite', {
            agent_name: agentName,
            test_names: testNames
        });
        return response.data;
    },

    generateTests: async (agentName: string, request: any) => {
        const response = await apiClient.post(`/design-and-save-tests/${agentName}`, request);
        return response.data;
    }
};

// ===== HEALTH CHECK API =====
export const healthApi = {
    check: async () => {
        const response = await apiClient.get('/health');
        return response.data;
    }
};

// ===== DATABASE TABLES API =====
export const databaseApi = {
    getTestResults: async () => {
        const response = await apiClient.get('/db/test-results');
        return response.data;
    },

    getAgentProfiles: async () => {
        const response = await apiClient.get('/db/agent-profiles');
        return response.data;
    },

    getAiInteractions: async () => {
        const response = await apiClient.get('/db/ai-interactions');
        return response.data;
    },

    getTables: async () => {
        const response = await apiClient.get('/db/tables');
        return response.data;
    }
};

// ===== ERROR HANDLING UTILITIES =====
export const handleApiError = (error: any): string => {
    if (error.response) {
        // Server responded with error status
        const message = error.response.data?.detail || error.response.data?.message || error.response.statusText;
        return `API Error (${error.response.status}): ${message}`;
    } else if (error.request) {
        // Request was made but no response received
        return 'Network Error: Unable to reach the server. Please check your connection.';
    } else {
        // Something else happened
        return `Request Error: ${error.message}`;
    }
};

// ===== RETRY UTILITY FOR IMPORTANT OPERATIONS =====
export const retryApiCall = async <T>(
    apiCall: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
): Promise<T> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await apiCall();
        } catch (error) {
            if (attempt === maxRetries) {
                throw error;
            }
            console.warn(` API call failed, retrying (${attempt}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
    }
    throw new Error('Max retries exceeded');
};

export const agentManagerApi = {
    upload: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);  // Note: backend expects 'file', not 'package'

        const response = await apiClient.post('/agent-manager/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    import: async (importData: any) => {
        const response = await apiClient.post('/agent-manager/import', importData);
        return response.data;
    },

    list: async () => {
        const response = await apiClient.get('/agent-manager/agents');
        return response.data;
    },

    export: async (agentId: string): Promise<Blob> => {
        const response = await apiClient.get(`/agent-manager/agents/${agentId}/export`, {
            responseType: 'blob'
        });
        return response.data;
    },

    delete: async (agentId: string) => {
        const response = await apiClient.delete(`/agent-manager/agents/${agentId}`);
        return response.data;
    },

    uploadIntelligent: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${getApiBaseUrl()}/agent-manager/upload-intelligent`, {
            method: 'POST',
            body: formData
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
        }

        return response.json();
    }

};


// Export default api client for custom calls
export default apiClient;
// Agent Manager Service
//export const getAgentManagerUrl = (): string => {
//    return process.env.REACT_APP_AGENT_MANAGER_URL || 'http://localhost:8007';
//};
